import { useState, useRef, useEffect } from "react";

export const IdentityConsentCard = ({ sessionId, visitorId, api, consentText, onVerified, defaultName, defaultEmail }) => {
  const [form, setForm] = useState({ name: defaultName || "", email: defaultEmail || "", phone: "", company: "", context: "" });
  const [consent, setConsent] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const mountedAt = useRef(Date.now());

  useEffect(() => { mountedAt.current = Date.now(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !consent) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${api}/contacts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          visitor_id: visitorId,
          ...form,
          consent_given: consent,
          website: honeypot,
          fill_ms: Date.now() - mountedAt.current,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "That didn't go through — please try again.");
      setDone(data.message);
      onVerified?.();
    } catch (err) {
      setError(err.message || "That didn't go through — please try again.");
    }
    setBusy(false);
  };

  const field = "rounded-xl bg-[#050505] border border-[#2E2E2E] px-4 py-3 text-base font-medium text-white placeholder:text-[#D6D6D6] outline-none focus:border-[#FF5C5C] w-full transition-colors duration-150";

  if (done) {
    return (
      <div className="rounded-2xl border border-[#2E2E2E] bg-[#111111] p-6 max-w-2xl" data-testid="identity-success">
        <p className="text-base font-medium text-white leading-[1.6]">{done}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="relative overflow-hidden rounded-2xl border border-[#2E2E2E] bg-[#111111] p-6 md:p-8 max-w-2xl space-y-4" data-testid="identity-card">
      <div className="absolute top-0 left-0 w-full h-1 bg-[#DC2626]" />
      <p className="text-base font-bold text-[#FF5C5C] pt-1">Your details</p>
      <p className="text-base font-medium text-[#D6D6D6] leading-[1.6]">
        Before we go further, I need your name, your email, and your written consent. It takes about twenty seconds.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input className={field} placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="identity-name-input" />
        <input className={field} type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="identity-email-input" />
        <input className={field} placeholder="Phone (for texts and calls)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="identity-phone-input" />
        <input className={field} placeholder="Company or project" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="identity-company-input" />
      </div>
      <textarea className={field} rows={2} placeholder="What's this about?" value={form.context} onChange={(e) => setForm({ ...form, context: e.target.value })} data-testid="identity-context-input" />
      <input
        type="text"
        name="website"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }}
      />
      <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-[#2E2E2E] bg-[#050505] p-4">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          data-testid="consent-checkbox"
          className="accent-[#DC2626] mt-1 shrink-0 w-4 h-4"
        />
        <span className="text-base font-medium text-[#D6D6D6] leading-[1.6]">{consentText}</span>
      </label>
      {error && <p className="text-base font-semibold text-[#FF5C5C]" data-testid="identity-error">{error}</p>}
      <button type="submit" disabled={busy || !form.name || !form.email || !consent} data-testid="identity-submit-button"
        className="rounded-xl bg-white text-black font-semibold text-base px-8 py-3 hover:bg-[#D6D6D6] disabled:opacity-40 transition-colors duration-150 w-full sm:w-auto">
        {busy ? "Sending..." : "Send my details"}
      </button>
    </form>
  );
};
